import { QueryKey, useQuery } from '@tanstack/react-query';
import { GetServerSideProps, GetServerSidePropsContext } from 'next';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';
import React, { useEffect } from 'react';

import ProductsPage from '../components/pages/products';
import { useAppContext } from '../components/providers/app-context';
import useLinkProps from '../hooks/use-link-props';
import {
  ADVANCED_VIEW_ACTIVE,
  DETAILED_PRICES,
  SHIPPING_LOCATION,
} from '../static/cookies';
import getCategoryNames from '../utils/get-category-names';
import { isGlobalUser } from '../utils/is-global-user';
import { isUKuser } from '../utils/is-uk-user';
import { isUSuser } from '../utils/is-us-user';
import getLoggedUser from '../utils/react-query/query-options/get-logged-user';
import getProducts from '../utils/react-query/query-options/get-products';
import getTranslatedSlugs from '../utils/react-query/query-options/get-translated-slugs';
import { redirectForAdvanced } from '../utils/redirect-for-advanced';
import slugChange301 from '../utils/slug-change-301';
import validateCategoryPartSlugs from '../utils/validate-category-part-slugs';
import { getSortedGroups } from '../utils/get-sorted-groups';
import getGroupsWithStocks from '../utils/react-query/query-options/get-groups-with-stock';
import { fetchVehicle } from '../services/vehicles';
import getVehicle from '../utils/react-query/query-options/get-vehicle';
import getUserSavedCars from '../utils/react-query/query-options/get-user-saved-cars';
import useToken from '../hooks/use-token';
import { getProductAvailableImages, getProductsMiles } from '../utils/kms-to-miles';
import forceShippingUKForCoUK from '../utils/force-shipping-uk-for-co-uk';

const useData = () => {
  const { locale = 'en', query, route } = useRouter();
  const { features, detailedPrice, shippingLocation } = useAppContext();
  const {
    vehicle = '',
    category = '',
    subCategory = '',
    sort = 'price-asc',
    page = '1',
    reference = '',
    priceFrom = '',
    priceTo = '',
  } = query as { [key: string]: string };
  const { t } = useTranslation();
  const getLink = useLinkProps();
  const csrftoken = useToken();

  const { data: groups } = useQuery(
    ...getGroupsWithStocks({
      locale,
      ...(vehicle ? { car_identifier: vehicle as string } : {}),
      ...(reference ? { reference: reference as string } : {}),
      ...(shippingLocation ? { shipping_location: shippingLocation } : {}),
    }),
    {
      keepPreviousData: true,
    }
  );

  let groupsSort: TCategories = [];
  if (groups) {
    groupsSort = getSortedGroups(groups);
  }

  const { data: translatedSlugs } = useQuery(
    ...getTranslatedSlugs({
      lang_origin: locale,
      lang_trans: null,
      slugs: [
        {
          slug_origin: category as string,
          slug_type: 'category',
        },
        {
          slug_origin: subCategory as string,
          slug_type: 'part',
        },
      ],
    }),
    {
      keepPreviousData: true,
      enabled: !!category && !!subCategory,
    }
  );

  const {
    data: products,
    isFetching: isProductsLoading,
    isFetched,
  } = useQuery<unknown, unknown, TProductSearch, QueryKey>(
    ...getProducts({
      locale,
      partIdentifier: subCategory,
      carIdentifier: vehicle,
      sort,
      page,
      reference,
      min_price: priceFrom,
      max_price: priceTo,
      ...(typeof detailedPrice === 'boolean' && {
        detailed_prices: detailedPrice ? 'true' : 'false',
      }),
    }),
    {
      keepPreviousData: true,
    }
  );

  let productsUptaded = products;
  if (products && process.env.NEXT_PUBLIC_HOST?.includes('.co.uk')) {
    productsUptaded = getProductsMiles(products);
  }

  if (products) {
    productsUptaded = getProductAvailableImages(products);
  }

  const {
    data: savedVehicles,
    isLoading: isVehiclesLoading,
    refetch: refetchVehicles,
  } = useQuery(...getUserSavedCars({ locale, csrftoken }), {
    keepPreviousData: true,
  });

  // GTM
  useEffect(() => {
    if (isFetched && products?.results.length > 0) {
      // @ts-ignore
      window.dataLayer.push({
        event: 'eec.impressionView',
        ecommerce: {
          currencyCode: 'EUR',
          impressions: products?.results.map((product, index) => ({
            list: 'Search Results',
            id: product.sku, // SKU of the product
            name: product.part_en, // name of the product.
            brand: product.car_brand, // car brand of the part
            category: product.category_en_old, // Product category(ies) - each "/" is a category level (max 5 levels)
            price: product.pricing?.price_product
              ? Number(product.pricing.price_product).toFixed(2)
              : null, // price of the part without shipping, tax and currency
            position: index, // the position of the item in the list
            dimension2: product.reference, // reference of the product
            dimension3: product.car_model, // car model of the part
            dimension4: product.car_version, // car version of the part
          })),
        },
      });

      // GA4
      window.dataLayer.push({ ecommerce: null }); // Clear the previous ecommerce object.
      window.dataLayer.push({
        event: 'view_item_list',
        ecommerce: {
          currency: 'EUR', // Currency of the items associated with the event, in 3-letter ISO 4217 format
          items: products?.results.map((product, index) => ({
            item_list_name: 'Search Results', // Name of the list in which the items were displayed.
            item_list_id: 'search-results', // Name of the list in which the items were displayed.
            item_id: product.sku, // SKU of the product
            item_name: product.part_en, // name of the product.
            item_brand: product.car_brand, // car brand of the part
            item_category: product.category_en, // Product category(ies) - each "/" is a category level (max 5 levels)
            item_category2: product.part_en, // part name === product name
            price: product.pricing?.price_product
              ? Number(product.pricing.price_product).toFixed(2)
              : null, // price of the part without shipping, tax and currency
            index: index, // the position of the item in the list
            quantity: '1', // Hardcoded to 1
          })),
        },
      });
    }
  }, [products, isFetched]);

  const isReferencePage = route === '/auto-parts/search';
  const breadcrumbs = [];

  if (products?.search?.car) {
    breadcrumbs.push(
      ...[
        !features.isBrandEnabled && {
          ...getLink('brands'),
          text: t('common:brand.other'),
        },
        {
          ...getLink('models', { brand: products.search.car.brand_slug }),
          text: products.search.car.brand_name,
        },
        products.search.car?.model_slug && {
          ...getLink('versions', {
            model: products.search.car.model_slug,
          }),
          text: `${products.search.car.model_name || ''} ${products.search.car?.version_name || ''
            }`,
        },
      ].filter(Boolean)
    );
  }

  const { data: vehicleData } = useQuery(...getVehicle({ locale, vehicle }), {
    enabled: !!vehicle,
  });

  if (products?.search?.part) {
    const categoriesNames = getCategoryNames(
      {
        category: products.search.part.subgroup_slug,
        subCategory: products.search.part.slug,
      },
      groups
    );

    if (products?.search?.car) {
      breadcrumbs.push(
        ...[
          {
            ...getLink('categories-with-vehicle', {
              category: products.search.car.slug,
            }),
            text: t('common:partscatalog'),
          },
          {
            ...getLink('sub-categories-with-vehicle', {
              category: products.search.car.slug,
              slug: products.search.part.subgroup_slug,
            }),
            text: `${categoriesNames['category']} ${products?.search?.reference
              ? ' - ' + categoriesNames['subCategory']
              : ''
              }`,
          },
        ].filter(Boolean)
      );
    } else {
      breadcrumbs.push({
        ...getLink('categories'),
        text: t('common:partscatalog'),
      });

      breadcrumbs.push({
        ...getLink('sub-categories', {
          category: products.search.part.subgroup_slug,
        }),
        text: `${categoriesNames['category']} - ${categoriesNames['subCategory']}`,
      });
    }
  }

  return {
    products: productsUptaded,
    groups: groupsSort || groups,
    breadcrumbs,
    isReferencePage,
    isLoading: isProductsLoading,
    translatedSlugs,
    vehicleData,
    savedVehicles,
    refetchVehicles,
  };
};

export default function Products({ groupsSort }) {
  const data = useData();

  return (
    <main>
      <ProductsPage {...data} />
    </main>
  );
}

export const getServerSideProps: GetServerSideProps = async ({
  locale = 'en',
  req,
  res,
  query: {
    vehicle = '',
    category = '',
    subCategory = '',
    sort = 'price-asc',
    page = '1',
    reference = '',
    priceFrom = '',
    priceTo = '',
    ...restQuery
  },
  params,
}) => {
  isUSuser(req, res);
  isUKuser(req, res);
  isGlobalUser(req, res);
  await forceShippingUKForCoUK({ req, res, locale });

  const { default: getGenericClient } = await import('../server/client');
  const genericClient = getGenericClient();
  const csrftoken = useToken();

  try {
    const [user, groups] = await Promise.all([
      genericClient.fetchQuery(...getLoggedUser({ locale }, req)),
      genericClient.fetchQuery(
        ...getGroupsWithStocks(
          {
            locale,
            ...(vehicle ? { car_identifier: vehicle as string } : {}),
            ...(reference ? { reference: reference as string } : {}),
            ...(req.cookies[SHIPPING_LOCATION]
              ? {
                shipping_location: parseInt(
                  req.cookies[SHIPPING_LOCATION],
                  10
                ),
              }
              : {}),
          },
          req
        )
      ),
      genericClient.fetchQuery(
        ...getUserSavedCars({ locale: locale as string, csrftoken }, req)
      ),
      category &&
      subCategory &&
      genericClient.prefetchQuery(
        ...getTranslatedSlugs(
          {
            lang_origin: locale,
            lang_trans: null,
            slugs: [
              {
                slug_origin: category as string,
                slug_type: 'category',
              },
              {
                slug_origin: subCategory as string,
                slug_type: 'part',
              },
            ],
          },
          req
        )
      ),
      //isReadyToSearchProducts &&
      await genericClient.fetchQuery(
        ...getProducts(
          {
            locale,
            partIdentifier: subCategory as string,
            carIdentifier: vehicle as string,
            sort: sort as string,
            page: page as string,
            reference: reference as string,
            min_price: priceFrom as string,
            max_price: priceTo as string,
            ...(!!req.cookies[DETAILED_PRICES] && {
              detailed_prices: req.cookies[DETAILED_PRICES] as string,
            }),
          },
          req
        )
      ),
      vehicle &&
      genericClient.fetchQuery(
        ...getVehicle(
          { locale: locale as string, vehicle: vehicle as string },
          req
        )
      ),
    ]);

    if (category || subCategory) {
      await validateCategoryPartSlugs({
        locale,
        groups,
        category: category as string,
        subCategory: subCategory as string,
        req,
      });
    }

    const { default: retrieveStateAndClear } = await import(
      '../utils/retrieve-state-and-clear'
    );

    if (
      user?.staff?.is_pro &&
      !!parseInt(req.cookies[ADVANCED_VIEW_ACTIVE] || '0')
    ) {
      return redirectForAdvanced({
        locale,
        category: category as string,
        subCategory: subCategory as string,
        vehicle: vehicle as string,
        otherQuery: {
          ...(!!sort && sort !== 'price-asc' ? { sort } : {}),
          ...(!!page && page !== '1' ? { page } : {}),
          reference,
          priceFrom,
          priceTo,
          ...restQuery,
        },
      });
    }

    let groupsSort: TCategories = [];
    if (groups) {
      groupsSort = getSortedGroups(groups);
    }

    return {
      props: {
        dehydratedState: retrieveStateAndClear(genericClient),
        shippingLocation: parseInt(req.cookies[SHIPPING_LOCATION], 10),
        detailPrices: req.cookies[DETAILED_PRICES] || null,
        groupsSort,
      },
    };
  } catch (e) {
    if (e.status === 301) {
      const newCategory = e.data.category || category;
      const newSubCategory = e.data.part || subCategory;
      const newVehicle = e.data?.version || e.data?.model || vehicle;

      const newQuery: { [key: string]: string } = {
        ...params,
        ...(newCategory && { category: newCategory }),
        ...(newSubCategory && { subCategory: newSubCategory }),
        ...(newVehicle && {
          vehicle: newVehicle,
        }),
      };

      return slugChange301({
        locale,
        query: newQuery,
        res,
        page: 'search',
      });
    } else if (e.status < 500) {
      if (e.status === 400 && e?.data.part_identifier) {
        return slugChange301({
          locale,
          query: {
            category: vehicle ? vehicle : category,
            ...(vehicle && { slug: category }),
          },
          res,
          page: vehicle ? 'sub-categories-with-vehicle' : 'sub-categories',
          status: 307,
        });
      }
      if (
        e?.data?.detail === 'part_identifier' ||
        e?.data?.detail === 'group_identifier'
      ) {
        // @ts-ignore
        const { category, subCategory, ...newQuery } = params;

        return slugChange301({
          locale,
          query: newQuery,
          res,
          page: 'search',
          status: 307,
        });
      }

      return {
        notFound: true,
      };
    }

    console.log(e);
    throw e;
  }
};
